// ============================================================
// PrincipalDashboard Component
// ============================================================

import { Box } from '@mui/material';
import { Users, GraduationCap, IndianRupee, UserPlus } from 'lucide-react';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { StatCard } from '@/components/cards/StatCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { RecentActivityCard } from '@/components/dashboard/RecentActivityCard';
import { QuickActionCard } from '@/components/dashboard/QuickActionCard';
import { AppLineChart } from '@/components/charts/AppLineChart';
import { AppBarChart } from '@/components/charts/AppBarChart';
import { WelcomeBanner } from '@/components/dashboard/WelcomeBanner';
import { SectionHeader } from '@/components/dashboard/SectionHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { useAuth } from '@/hooks/useAuth';
import { useSchool } from '@/hooks/useSchool';
import useDashboardStore from '@/store/dashboardStore';
import { AnalyticsFilter } from '@/components/filters/AnalyticsFilter';
import { useNavigate } from 'react-router-dom';
import useLeaveStore from '@/store/leaveStore';
import { useEffect } from 'react';

export function PrincipalDashboard({ stats, charts, activities }) {
  const { user } = useAuth();
  const { currentSchool } = useSchool();
  const dateRange = useDashboardStore((state) => state.dateRange);
  const setDateRange = useDashboardStore((state) => state.setDateRange);
  const navigate = useNavigate();

  const { summary: leaveSummary, fetchSummary } = useLeaveStore();
  
  useEffect(() => {
    fetchSummary('principal');
  }, []);

  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);

  return (
    <Box>
      <WelcomeBanner 
        user={user} 
        currentSchool={currentSchool} 
        action={<AnalyticsFilter value={dateRange} onChange={setDateRange} sx={{ bgcolor: 'background.paper', borderRadius: 1 }} />}
      />

      <SectionHeader title="School Overview" />
      <DashboardGrid columns={{ xs: 1, sm: 2, lg: 5 }}>
        <StatCard
          title="Total Students"
          value={stats.totalStudents?.toLocaleString()}
          icon={GraduationCap}
          color="primary"
          trend="up"
          trendValue="+3.1%"
          onClick={() => navigate('/students/list')}
        />
        <StatCard
          title="Total Teachers"
          value={stats.totalTeachers?.toLocaleString() || '0'}
          icon={Users}
          color="secondary"
          onClick={() => navigate('/teachers/list')}
        />
        <StatCard
          title="New Registrations"
          value={stats.newAdmissions?.toLocaleString() || '0'}
          icon={UserPlus}
          color="info"
          trend="up"
          trendValue="+12%"
          onClick={() => navigate('/students/list', { state: { admissionType: 'New' } })}
        />
        <StatCard
          title="Fee Collection"
          value={formatCurrency(stats.monthlyCollection)}
          icon={IndianRupee}
          color="success"
          onClick={() => navigate('/fees')}
        />
        <StatCard
          title="Pending Fees"
          value={formatCurrency(stats.pendingFees || 0)}
          icon={IndianRupee}
          color="error"
          trend="down"
          trendValue="-2.4%"
          onClick={() => navigate('/fees/collect')}
        />
      </DashboardGrid>

      <DashboardGrid columns={{ xs: 1, lg: 2 }}>
        <ChartCard title="Student Growth">
          <AppLineChart data={charts.studentGrowth} xAxisKey="year" dataKey="students" />
        </ChartCard>
        <ChartCard title="Attendance Overview">
          <AppBarChart data={charts.attendanceOverview} xAxisKey="day" dataKey="present" />
        </ChartCard>
      </DashboardGrid>

      <DashboardGrid columns={{ xs: 1, lg: 3 }}>
        <Box sx={{ gridColumn: { lg: 'span 2' } }}>
          <RecentActivityCard activities={activities} />
        </Box>
        <WidgetContainer title="Quick Actions">
          <Box sx={{ p: 1 }}>
            <DashboardGrid columns={2} gap={1}>
              <QuickActionCard title="Manage Students" icon={GraduationCap} color="primary" path="/students" />
              <QuickActionCard title="Manage Fees" icon={IndianRupee} color="success" path="/fees" />
              <QuickActionCard title="Teachers" icon={Users} color="secondary" path="/teachers" />
              <QuickActionCard 
                title="Leave Approvals" 
                icon={Users} 
                color="warning" 
                path="/leaves"
                badge={leaveSummary?.pending > 0 ? leaveSummary.pending : null}
              />
            </DashboardGrid>
          </Box>
        </WidgetContainer>
      </DashboardGrid>
    </Box>
  );
}
